import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Briefcase, Search, MapPin, Building2, Home, Laptop, Clock, 
  DollarSign, GraduationCap, UserPlus, Eye, Share2, ExternalLink,
  ChevronRight, Sparkles, TrendingUp, Filter, X
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthToken = () => localStorage.getItem('token');

// Work type badge colors
const getWorkTypeBadgeStyle = (workType) => {
  switch (workType) {
    case 'Work from Home':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Work from Office':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Hybrid':
      return 'bg-violet-100 text-violet-700 border-violet-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

// Work type icon
const WorkTypeIcon = ({ type }) => {
  switch (type) {
    case 'Work from Home':
      return <Home className="h-3.5 w-3.5" />;
    case 'Work from Office':
      return <Building2 className="h-3.5 w-3.5" />;
    case 'Hybrid':
      return <Laptop className="h-3.5 w-3.5" />;
    default:
      return <Briefcase className="h-3.5 w-3.5" />;
  }
};

// Hero Banner Component
const HeroBanner = ({ banner }) => {
  const bgStyle = banner?.background_type === 'image' && banner?.image_url
    ? { backgroundImage: `url(${banner.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: `linear-gradient(135deg, ${banner?.gradient_from || '#4C1D95'} 0%, ${banner?.gradient_to || '#F97316'} 100%)` };

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-xl"
      style={bgStyle}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20" />
      
      {/* Content */}
      <div className="relative z-10 p-8 md:p-12">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-4">
            {banner?.title || 'Find Your Next Opportunity'}
          </h1>
          {banner?.subtitle && (
            <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed mb-6">
              {banner.subtitle}
            </p>
          )}
          {banner?.cta_text && (
            <Button 
              size="lg"
              className="bg-white text-[#4C1D95] hover:bg-white/90 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
              data-testid="hero-cta-button"
            >
              {banner.cta_text}
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          )}
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
          <Sparkles className="h-4 w-4 text-white" />
          <span className="text-white/90 text-sm font-medium">Refer & Earn</span>
        </div>
      </div>
    </div>
  );
};

// Modern Job Card Component
const JobCard = ({ job, onView, onRefer, onShare, index, companies = [] }) => {
  // Get company logo from companies list if not directly on job
  const getCompanyLogo = () => {
    if (job.company_logo || job.company_logo_url) {
      return job.company_logo || job.company_logo_url;
    }
    // Look up company by name
    const company = companies.find(c => c.name === job.company_name);
    return company?.logo_url || null;
  };
  
  const companyLogo = getCompanyLogo();
  
  return (
    <Card 
      className="group bg-white border border-slate-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`job-card-${job.id}`}
    >
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-slate-900 truncate group-hover:text-[#4C1D95] transition-colors">
              {job.position_name || 'Open Position'}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-600 truncate">{job.company_name || 'Company'}</span>
            </div>
          </div>
          
          {/* Company Logo */}
          {companyLogo ? (
            <img 
              src={`${API?.replace('/api', '')}${companyLogo}`}
              alt={job.company_name}
              className="h-12 w-12 rounded-xl object-cover border border-slate-200"
            />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#4C1D95] to-[#F97316] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {(job.company_name || 'C').charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Tags Row */}
        <div className="flex flex-wrap gap-2 mb-4">
          {job.work_type && (
            <Badge variant="outline" className={`${getWorkTypeBadgeStyle(job.work_type)} flex items-center gap-1 rounded-lg px-2.5 py-1`}>
              <WorkTypeIcon type={job.work_type} />
              <span className="text-xs font-medium">{job.work_type === 'Work from Home' ? 'WFH' : job.work_type === 'Work from Office' ? 'Office' : job.work_type}</span>
            </Badge>
          )}
          {job.location && (
            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 flex items-center gap-1 rounded-lg px-2.5 py-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">{job.location}</span>
            </Badge>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4">
          {job.salary_range && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-semibold text-emerald-700">{job.salary_range}</span>
            </div>
          )}
          {(job.education || job.experience) && (
            <div className="flex flex-wrap gap-3">
              {job.education && (
                <div className="flex items-center gap-1.5">
                  <GraduationCap className="h-4 w-4 text-amber-500" />
                  <span className="text-xs text-slate-600">{job.education}</span>
                </div>
              )}
              {job.experience && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-slate-600">{job.experience}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Description Preview */}
        {job.job_description && (
          <p className="text-sm text-slate-500 line-clamp-2 mb-4">
            {job.job_description}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => onView(job)}
            className="rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
            data-testid={`view-job-${job.id}`}
          >
            <Eye className="h-4 w-4 mr-1.5" />
            View
          </Button>
          <Button 
            size="sm"
            onClick={() => onRefer(job)}
            className="flex-1 bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#DC2626] text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
            data-testid={`refer-job-${job.id}`}
          >
            <UserPlus className="h-4 w-4 mr-1.5" />
            Refer
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-xl border-slate-200 hover:bg-slate-50"
                data-testid={`share-job-${job.id}`}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl p-2">
              <button 
                onClick={() => onShare(job, 'whatsapp')} 
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 rounded-lg transition-colors"
              >
                <span className="text-green-500 font-medium">WhatsApp</span>
              </button>
              <button 
                onClick={() => onShare(job, 'linkedin')} 
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 rounded-lg transition-colors"
              >
                <span className="text-blue-600 font-medium">LinkedIn</span>
              </button>
              <button 
                onClick={() => onShare(job, 'copy')} 
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Copy Link</span>
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Jobs Dashboard Component
const JobsDashboard = ({ 
  jobs = [], 
  onViewJob, 
  onReferJob, 
  onShareJob,
  companies = [] 
}) => {
  const [heroBanner, setHeroBanner] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [workTypeFilter, setWorkTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeroBanner();
  }, []);

  const fetchHeroBanner = async () => {
    try {
      const response = await axios.get(`${API}/job-banners/active`);
      setHeroBanner(response.data);
    } catch (error) {
      console.error('Failed to fetch banner:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchQuery || 
      (job.position_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (job.company_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (job.location?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesWorkType = workTypeFilter === 'all' || job.work_type === workTypeFilter;
    
    return matchesSearch && matchesWorkType;
  });

  // Count by work type
  const workTypeCounts = {
    all: jobs.length,
    'Work from Home': jobs.filter(j => j.work_type === 'Work from Home').length,
    'Work from Office': jobs.filter(j => j.work_type === 'Work from Office').length,
    'Hybrid': jobs.filter(j => j.work_type === 'Hybrid').length,
  };

  return (
    <div className="space-y-8">
      {/* Hero Banner */}
      <HeroBanner banner={heroBanner} />

      {/* Stats Bar */}
      <div className="flex items-center justify-between bg-white rounded-2xl p-4 shadow-sm border border-slate-200/50">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4C1D95] to-[#7C3AED] flex items-center justify-center">
              <Briefcase className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{jobs.length}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Open Jobs</p>
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            <span className="text-sm text-slate-600">
              <span className="font-semibold text-emerald-600">{filteredJobs.length}</span> matching your search
            </span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Search jobs by title, company, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-4 py-3 h-12 rounded-xl border-slate-200 bg-white shadow-sm focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95]"
            data-testid="job-search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Work Type Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Jobs', icon: Briefcase, color: 'from-[#4C1D95] to-[#7C3AED]' },
            { value: 'Work from Home', label: 'WFH', icon: Home, color: 'from-emerald-500 to-green-600' },
            { value: 'Work from Office', label: 'Office', icon: Building2, color: 'from-blue-500 to-indigo-600' },
            { value: 'Hybrid', label: 'Hybrid', icon: Laptop, color: 'from-violet-500 to-purple-600' },
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setWorkTypeFilter(filter.value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                workTypeFilter === filter.value
                  ? `bg-gradient-to-r ${filter.color} text-white shadow-lg`
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
              data-testid={`filter-${filter.value}`}
            >
              <filter.icon className="h-4 w-4" />
              {filter.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-md text-xs ${
                workTypeFilter === filter.value ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {workTypeCounts[filter.value]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Jobs Grid */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No jobs found</h3>
          <p className="text-slate-500 max-w-sm mx-auto">
            {searchQuery || workTypeFilter !== 'all' 
              ? 'Try adjusting your search or filters to find more opportunities.'
              : 'No job openings available at the moment. Check back soon!'}
          </p>
          {(searchQuery || workTypeFilter !== 'all') && (
            <Button 
              variant="outline" 
              className="mt-4 rounded-xl"
              onClick={() => { setSearchQuery(''); setWorkTypeFilter('all'); }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredJobs.map((job, index) => (
            <JobCard
              key={job.id}
              job={job}
              index={index}
              companies={companies}
              onView={onViewJob}
              onRefer={onReferJob}
              onShare={onShareJob}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsDashboard;
